import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { debounce } from 'lodash';
import DataTable from 'react-data-table-component';
import { useNavigation } from "@react-navigation/native";
import Constants from "expo-constants";
import { ActivityIndicator, Modal, Portal, Provider, Menu } from "react-native-paper";
import { Ionicons } from '@expo/vector-icons';
import { ViewHorizontal, Row, ViewTable, ContainerScroll } from "./styles";
import { Title, SmallText, SubTitle, StandardText, FooterText } from '../../../config/theme/globalStyles';
import THEME from '../../../config/theme';
import AdminButton from '../../../components/AdminButton';
import AdminInput from '../../../components/AdminInput';
import TouchableText from '../../../components/TouchableText';
import { ADMIN_COLORS, ADMIN_FONT } from '../../../config/theme/adminColors';
import { Badge } from '../../AdminPanel/Products/formStyles';
import CreateUserModal from "../../AdminPanel/AppUsers/CreateUserModal";
import { ResetPassword } from "../../Login/ResetPassword";
import { TouchableOpacity, Text, TextInput, useWindowDimensions } from 'react-native';
import View from '@expo/html-elements/build/primitives/View';
import { firestore } from "../../../services/firebase";
import PQueue from 'p-queue';
import { doc, getDoc, collection, getDocs, query, where, limit } from "firebase/firestore";

import { formatDateBR, timeAgo } from '../../../utils/dateUtils';
import { generateCSVContent } from '../../../utils/csvUtils';
import { getPlatformForProduct, getProductName, getSignupDate, originStampIsStale } from '../../../utils/accessUtils';

const Table = ({
    tableData,
    products,
    totalCount,
    loading,
    totalFiltered,
    stats,
    statsLoading,
    handleSaveUser,
    handlePageChange,
    handleFilter,
    getSuggestions,
    isSearchResults,
    directoryReady,
    resetPaginationToggle,
    fetchAllUsersHandler,
    onComputeStats
}) => {
    // "…" só enquanto NÃO há número nenhum. Havendo parcial (varredura em
    // andamento) ou a última apuração guardada, mostramos o valor.
    const statKpi = (value) =>
        typeof value === 'number'
            ? value.toLocaleString('pt-BR')
            : (statsLoading ? '…' : '—');

    // Status curto dentro do botão "Apurar" (2ª linha): o progresso da varredura (numa
    // base grande ela leva minutos e parecia travada) ou de quando é a última
    // apuração, para ninguém tomar um número de ontem por tempo real.
    const statsStatus = statsLoading
        ? (stats?.scanned
            ? `${stats.scanned.toLocaleString('pt-BR')}${totalCount ? ` de ${totalCount.toLocaleString('pt-BR')}` : ''} alunos`
            : null)
        : stats?.stale
            ? 'Números podem estar defasados'
            : stats?.updatedAt
                ? `Apurado ${timeAgo(stats.updatedAt) || 'agora'}`
                // Guardado por uma versão antiga, sem data.
                : typeof stats?.active === 'number' ? 'Apuração sem data' : null;

    // Abaixo de 768px os quatro cards de indicador empilham e a barra de busca
    // ocupa a linha inteira — em 360px eles saíam pela borda.
    const { width: larguraTela } = useWindowDimensions();
    const isMobile = larguraTela < 768;

    const [selectedRows, setSelectedRows] = useState([]);
    const [isCreateUserModalVisible, setIsCreateUserModalVisible] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [visible, setVisible] = useState(false);
    const [isDownloadAllModalVisible, setIsDownloadAllModalVisible] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [menuVisible, setMenuVisible] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Recalcula as sugestões (autocomplete) para o termo atual — usado quando o
    // diretório termina de carregar depois que o admin já digitou.
    const refreshSuggestions = (term) => {
        const s = getSuggestions ? getSuggestions(term) : [];
        setSuggestions(s);
        setShowSuggestions(!!term && term.trim().length >= 2 && s.length > 0);
    };
    useEffect(() => {
        if (searchFocused && searchText) refreshSuggestions(searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [directoryReady]);

    const handleSearchChange = (t) => {
        setSearchText(t);
        debouncedFilter(t);
        refreshSuggestions(t);
    };

    const pickSuggestion = (item) => {
        setSearchText(item.email || item.name);
        setShowSuggestions(false);
        debouncedFilter.cancel();
        handleFilter(item.email || item.name);
    };

    const clearSearch = () => {
        setSearchText('');
        setSuggestions([]);
        setShowSuggestions(false);
        debouncedFilter.cancel();
        handleFilter('');
    };

    // Busca automática (debounce): dispara ~450ms após parar de digitar, sem Enter.
    // Mantemos o handleFilter mais recente num ref para não recriar o debounce a cada render.
    const handleFilterRef = useRef(handleFilter);
    useEffect(() => { handleFilterRef.current = handleFilter; });
    const debouncedFilter = useMemo(
        () => debounce((value) => handleFilterRef.current(value), 450),
        []
    );
    useEffect(() => () => debouncedFilter.cancel(), [debouncedFilter]);

    const { guruUserToken } = Constants.manifest.extra;
    const navigation = useNavigation();

    const [productsCache, setProductsCache] = useState({});

    // Função para buscar o nome do produto Guru na coleção 'products'
    const fetchProductName = useCallback(
        async (productId) => {
            // Se já existe no cache, retorna imediatamente
            if (productsCache[productId]) {
                return productsCache[productId];
            }

            let nameFound = productId; // fallback se não achar nada

            try {
                // Verifica se é Guru (ID numérico)
                if (/^\d+$/.test(productId)) {
                    // GURU => query por productId == productId
                    const q = query(
                        collection(firestore, 'products'),
                        where('productId', '==', productId),
                        limit(1)
                    );
                    const querySnapshot = await getDocs(q);
                    if (!querySnapshot.empty) {
                        const docData = querySnapshot.docs[0].data();
                        nameFound = docData?.name || productId;
                    }
                }
                // Verifica se é Pagarme (20 caracteres alfanuméricos)
                else if (/^[A-Za-z0-9]{20}$/.test(productId)) {
                    // PAGARME => docRef = doc(firestore, 'products', productId)
                    const docRef = doc(firestore, 'products', productId);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const docData = docSnap.data();
                        nameFound = docData?.name || productId;
                    }
                }
                // Se não for nem Guru nem Pagarme, fica com o fallback

            } catch (error) {
                console.error("Erro ao buscar nome do produto:", error);
            }

            // Salva no cache para evitar buscas repetidas
            setProductsCache((prev) => ({
                ...prev,
                [productId]: nameFound,
            }));

            return nameFound;
        },
        [productsCache]
    );

    // Função que define a forma de cadastro
    const formatAccess = useCallback((row) => {
        if (row?.isSignUpFromApp) return "Aplicativo Mobile";
        // A flag do painel só vale quando aquela gravação criou a conta: o
        // createUser antigo carimbava `isSignUpFromAdminPanel` em doc que já
        // existia, e quem se cadastrou sozinho virava "Manualmente" (ticket).
        if (row?.isSignUpFromAdminPanel && !originStampIsStale(row)) return "Manualmente";
        return "Aplicativo Web";
    }, []);

    // Verificações de validade de acesso
    const hasValidPlan = useCallback((row) => row.planTitle && row.plan !== "0", []);

    const hasValidProduct = useCallback((row) => {
        return row.productIds?.some(product => {
            const expirationDate = product.expirationDate instanceof Date
                ? product.expirationDate
                : product.expirationDate?.toDate?.();
            return product.productId && (!expirationDate || expirationDate > new Date());
        });
    }, []);

    const hasValidCourse = useCallback((row) => {
        return row.courses?.some(course => {
            const expirationDate = course.dueDate instanceof Date
                ? course.dueDate
                : course.dueDate?.toDate?.();
            return expirationDate > new Date();
        });
    }, []);

    // Função que define o status de acesso geral
    const getAccessStatus = useCallback((row, productIndex = null, forCSV = false) => {
        if (hasValidPlan(row) || hasValidProduct(row) || hasValidCourse(row)) {
            return 'Ativo';
        }
        if (forCSV && productIndex !== null) {
            const now = new Date();
            const product = row.productIds?.[productIndex] || {};
            const course = row.courses?.[productIndex] || {};
            const expirationDate = product.expirationDate?.toDate
                ? product.expirationDate.toDate()
                : course.dueDate?.toDate
                    ? course.dueDate.toDate()
                    : null;
            if ((product.transactionIdentifier || product.productId) && !expirationDate) {
                return 'Ativo';
            }
            return expirationDate > now ? 'Ativo' : 'Acesso expirado';
        }
        return 'Sem compra ativa';
    }, [hasValidPlan, hasValidProduct, hasValidCourse]);

    // Mapa productId -> { name, marketplace, type } para resolver TÍTULO e TIPO do acesso.
    const productMap = useMemo(() => {
        const map = {};
        (products || []).forEach((p) => { if (p?.productId != null) map[String(p.productId)] = p; });
        return map;
    }, [products]);

    // Agrega informações de acesso
    const getProductAccess = useCallback((row, forCSV = false) => {
        const accessInfo = [];

        if (hasValidPlan(row)) {
            const accessEntry = forCSV
                ? { type: "Plano Stripe", name: row.planTitle, dueDate: null }
                : `Stripe - ${row.planTitle}`;
            accessInfo.push(accessEntry);
        }

        if (row.coursesTitle?.length > 0) {
            accessInfo.push(...row.coursesTitle.map((course, index) => {
                const courseData = row.courses?.[index] || {};
                return forCSV
                    ? { type: "Curso Stripe", name: course, dueDate: courseData.dueDate ? formatDateBR(courseData.dueDate) : null }
                    : `Stripe - ${course}`;
            }));
        }

        if (row.productIds?.length > 0) {
            accessInfo.push(...row.productIds.map(product => {
                // Resolve o NOME do produto (Guru/Pagarme/IAP) pela coleção `products`;
                // só cai no getProductName (derivado do código) se não houver cadastro.
                const info = productMap[String(product.productId)];
                const productName = info?.name || getProductName(product.productId);
                // expirationDate pode ser Timestamp, Date ou string em docs legados —
                // .toDate() incondicional derrubava o CSV inteiro num único doc fora do padrão.
                const rawExpiration = product.expirationDate;
                const expirationAsDate = rawExpiration instanceof Date
                    ? rawExpiration
                    : (rawExpiration?.toDate?.() ?? null);
                return forCSV
                    ? { type: "", name: productName, dueDate: expirationAsDate }
                    : productName;
            }));
        }

        return forCSV ? accessInfo : (accessInfo.length ? accessInfo.join(' / ') : '-');
    }, [hasValidPlan, productMap]);

    const productAccess = useCallback((row) => getProductAccess(row, false), [getProductAccess]);
    const productAccessForCSV = useCallback((row) => getProductAccess(row, true), [getProductAccess]);

    // Lista de acessos do aluno com título + tipo (Assinatura / Produto / Curso), para
    // exibir o nome (não o código) na coluna e o pop-over.
    const getAccessItems = useCallback((row) => {
        const items = [];
        if (row.planTitle && row.plan !== "0") {
            items.push({ name: row.planTitle, kind: 'Assinatura' });
        }
        if (row.coursesTitle?.length > 0) {
            row.coursesTitle.forEach((c) => items.push({ name: c, kind: 'Curso' }));
        }
        if (row.productIds?.length > 0) {
            row.productIds.forEach((p) => {
                const info = productMap[String(p.productId)];
                const name = info?.name || getProductName(p.productId);
                const kind = info?.type === 'plan'
                    ? 'Assinatura'
                    : info?.type === 'course' || info?.marketplace === 'Curso'
                        ? 'Curso'
                        : info?.marketplace === 'Plano'
                            ? 'Assinatura'
                            : 'Produto';
                items.push({ name, kind });
            });
        }
        return items;
    }, [productMap]);

    // Célula da coluna "Acessos": mostra o título; ao passar o mouse, um pop-over
    // (position: fixed p/ escapar do overflow da tabela) lista nome + tipo de cada acesso.
    const AccessCell = useCallback(({ items }) => {
        const [rect, setRect] = useState(null);
        if (!items.length) {
            return <SmallText color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT}>—</SmallText>;
        }
        const summary = items.length === 1 ? items[0].name : `${items[0].name}  +${items.length - 1}`;
        const badgeVariant = (kind) => (kind === 'Assinatura' ? 'subscription' : kind === 'Curso' ? 'info' : 'product');

        // Ancora o pop-over à CÉLULA (ao lado da linha), não ao ponteiro do mouse.
        const POP_W = 320;
        let popStyle = null;
        if (rect && typeof window !== 'undefined') {
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            let left = rect.right + 8;                                   // abre à direita da célula
            if (left + POP_W > vw - 8) left = Math.max(8, rect.left - POP_W - 8); // vira p/ esquerda se não couber
            const approxH = Math.min(items.length * 40 + 24, vh - 24);
            let top = rect.top;                                          // alinhado ao topo da linha
            if (top + approxH > vh - 8) top = Math.max(8, vh - approxH - 8);      // não estoura embaixo
            popStyle = { left, top, maxHeight: vh - 24 };
        }

        return (
            <View
                style={{ maxWidth: '100%' }}
                onMouseEnter={(e) => {
                    const el = e?.currentTarget;
                    if (el && el.getBoundingClientRect) setRect(el.getBoundingClientRect());
                }}
                onMouseLeave={() => setRect(null)}
            >
                <Text numberOfLines={1} style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.ink }}>
                    {summary}
                </Text>
                {popStyle && (
                    // Portal (raiz do app) para escapar do contexto de empilhamento
                    // da tabela — antes o pop-over ficava ATRÁS das células
                    // "Cadastro"/paginação, deixando o texto ilegível.
                    <Portal>
                        <View
                            style={{
                                position: 'fixed',
                                left: popStyle.left,
                                top: popStyle.top,
                                width: POP_W,
                                maxHeight: popStyle.maxHeight,
                                overflow: 'auto',
                                zIndex: 99999,
                                backgroundColor: ADMIN_COLORS.surface,
                                borderWidth: 1,
                                borderColor: ADMIN_COLORS.border,
                                borderRadius: 10,
                                padding: 12,
                                gap: 8,
                                boxShadow: '0 12px 32px rgba(16,24,40,0.24)',
                            }}
                        >
                            {items.map((it, i) => (
                                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                    <Badge variant={badgeVariant(it.kind)}>{it.kind}</Badge>
                                    <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.ink, flexShrink: 1 }} numberOfLines={2}>
                                        {it.name}
                                    </Text>
                                </View>
                            ))}
                        </View>
                    </Portal>
                )}
            </View>
        );
    }, []);

    // Busca informações de plataforma e data de compra
    const fetchPlatformAndDate = useCallback(async (userDoc, product) => {
        try {
            let platform = "-";
            let currentDataCycle = "-";

            if (!product.transactionIdentifier) {
                return { platform: getPlatformForProduct(product), currentDataCycle };
            } else {
                const docRef = doc(firestore, 'users', userDoc.id, 'subscriptions', product.transactionIdentifier);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const subData = docSnap.data();
                    platform = subData.store === "PLAY_STORE"
                        ? "Play Store"
                        : subData.store === "APP_STORE"
                            ? "Apple Store"
                            : "-";
                    currentDataCycle = subData.purchased_at_ms
                        ? formatDateBR(new Date(subData.purchased_at_ms))
                        : "-";
                }
            }
            return { platform, currentDataCycle };
        } catch (error) {
            console.error("Erro ao buscar dados da compra:", error);
            return { platform: "-", currentDataCycle: "-" };
        }
    }, []);

    const showModal = useCallback(() => setVisible(true), []);
    const hideModal = useCallback(() => setVisible(false), []);

    const containerStyle = useMemo(() => ({
        backgroundColor: "white",
        padding: "2rem",
        width: Math.round(window.innerWidth * 0.7),
        alignSelf: "center",
    }), []);

    // Definição das colunas da tabela
    const columns = useMemo(() => [
        { name: 'Nome', selector: row => row.Nome_Completo || '', sortable: true, grow: 1.4, wrap: true },
        { name: 'Email', selector: row => row.Email || '', sortable: true, grow: 1.6 },
        {
            name: 'Status',
            selector: row => getAccessStatus(row),
            sortable: true,
            width: '120px',
            cell: row => {
                const active = getAccessStatus(row) === 'Ativo';
                const hasAnyPurchase = !!(row.productIds?.length || row.courses?.length || (row.planTitle && row.plan !== "0"));
                return (
                    <ViewHorizontal>
                        <Badge variant={active ? 'success' : (hasAnyPurchase ? 'danger' : 'neutral')}>
                            {active ? 'Ativo' : (hasAnyPurchase ? 'Expirado' : 'Sem compra')}
                        </Badge>
                    </ViewHorizontal>
                );
            },
        },
        {
            name: 'Acessos',
            selector: row => getAccessItems(row).map(i => i.name).join(', '),
            sortable: true,
            grow: 2,
            cell: row => <AccessCell items={getAccessItems(row)} />,
        },
        {
            name: 'Vídeos',
            selector: row => row.watchedVideosCount ?? row.watchedContents?.length ?? 0,
            width: '110px',
            cell: row => {
                const count = row.watchedVideosCount ?? row.watchedContents?.length ?? 0;
                return (
                    <ViewHorizontal>
                        <SmallText color={ADMIN_COLORS.ink} fontFamily={ADMIN_FONT} fontSize={THEME.FONTSIZE.EXTRASMALL}>
                            {count}
                        </SmallText>
                        {count > 0 && (
                            <TouchableText
                                onPress={() => navigation.navigate("UsersAnalytics", { userId: row.id, userName: row.Nome_Completo || '' })}
                                title="Ver"
                                textDecoration="underline"
                                color={ADMIN_COLORS.teal}
                                margin="0rem 0.5rem"
                                fontFamily={ADMIN_FONT}
                                fontSize={THEME.FONTSIZE.EXTRASMALL}
                            />
                        )}
                    </ViewHorizontal>
                );
            },
            sortable: true,
        },
        {
            // "no app" para não ser confundida com a data da compra na Guru/Stripe:
            // esta coluna é a criação da conta do aluno (ticket de suporte).
            name: 'Cadastro no app',
            // Ordena pelo timestamp cru — a string dd/mm/aaaa ordenaria pelo dia do mês.
            selector: row => getSignupDate(row)?.getTime() ?? 0,
            sortable: true,
            cell: row => (
                <View>
                    <SmallText color={ADMIN_COLORS.ink} fontFamily={ADMIN_FONT} fontSize={THEME.FONTSIZE.EXTRASMALL}>
                        {formatDateBR(getSignupDate(row))}
                    </SmallText>
                    <FooterText color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT}>
                        {formatAccess(row)}
                    </FooterText>
                </View>
            ),
        },
        {
            name: 'Ações',
            width: '130px',
            cell: row => (
                <TouchableText
                    onPress={() => navigation.navigate("MeuPerfil", { userId: row.id, editUserAccess: true })}
                    title="Editar acesso"
                    fontFamily={ADMIN_FONT}
                    fontSize={THEME.FONTSIZE.EXTRASMALL}
                    textDecoration="underline"
                    color={ADMIN_COLORS.teal}
                />
            ),
        },
    ], [getAccessStatus, getAccessItems, AccessCell, formatAccess, navigation]);

    const customStyles = useMemo(() => ({
        table: {
            style: {
                backgroundColor: ADMIN_COLORS.surface,
                borderRadius: '12px',
                border: `1px solid ${ADMIN_COLORS.border}`,
                overflow: 'hidden',
            },
        },
        headRow: {
            style: {
                backgroundColor: ADMIN_COLORS.surfaceSubtle,
                borderBottomColor: ADMIN_COLORS.border,
                minHeight: '46px',
            },
        },
        headCells: {
            style: {
                color: ADMIN_COLORS.muted,
                fontFamily: ADMIN_FONT,
                fontSize: '11px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
            },
        },
        rows: {
            style: {
                color: ADMIN_COLORS.ink,
                fontFamily: ADMIN_FONT,
                fontSize: '13px',
                minHeight: '56px',
                borderBottomColor: ADMIN_COLORS.border,
            },
            highlightOnHoverStyle: {
                backgroundColor: ADMIN_COLORS.surfaceSubtle,
                borderBottomColor: ADMIN_COLORS.border,
                outline: 'none',
            },
        },
        cells: { style: { paddingTop: '8px', paddingBottom: '8px' } },
        pagination: {
            style: {
                fontFamily: ADMIN_FONT,
                color: ADMIN_COLORS.muted,
                borderTopColor: ADMIN_COLORS.border,
            },
        },
    }), []);

    // Processa os dados para geração do CSV, tratando cada acesso individualmente
    const handleDownloadCSV = useCallback(async (rows) => {
        const headers = [
            'Nome_Completo', 'Email', 'Celular', 'CPF', 'Tipo_De_Acesso',
            'Acesso', 'Plataforma', 'Data_Ciclo_Atual', 'Data_Validade_Acesso',
            'Vídeos_Assistidos', 'Data_de_Cadastro', 'Forma_de_Cadastro'
        ];

        const selectedData = [];

        for (const row of rows) {
            const userData = { id: row.id, data: () => row };
            const accesses = productAccessForCSV(row);

            if (accesses.length === 0) {
                selectedData.push({
                    Nome_Completo: row.Nome_Completo || '-',
                    Email: row.Email || '-',
                    Celular: row.Celular || '-',
                    CPF: row?.CPF || row?.Doc || '-',
                    Tipo_de_Acesso: 'Sem compras',
                    Acesso: '-',
                    Plataforma: '-',
                    Data_Ciclo_Atual: '-',
                    Data_Validade_Acesso: '-',
                    Vídeos_Assistidos: row.watchedVideosCount ?? row.watchedContents?.length ?? 0,
                    Data_de_Cadastro: formatDateBR(getSignupDate(row)) || '-',
                    Forma_de_Cadastro: formatAccess(row) || '-',
                });
            } else {
                for (let i = 0; i < accesses.length; i++) {
                    const access = accesses[i];
                    let statusAccess = '';
                    let validade = access.dueDate ? access.dueDate : null;
                    let platform = "-";
                    let currentDataCycle = "-";

                    if (access.type === "Plano Stripe") {
                        statusAccess = 'Ativo';
                        platform = "Stripe";
                    } else if (access.type === "Curso Stripe") {
                        const now = new Date();
                        let validadeDate = null;
                        if (typeof validade === 'string') {
                            const [day, month, year] = validade.split('/');
                            validadeDate = new Date(`${year}-${month}-${day}T23:59:59`);
                        } else {
                            validadeDate = validade;
                        }
                        statusAccess = validadeDate && validadeDate > now ? 'Ativo' : 'Acesso expirado';
                        platform = "Stripe";
                    } else {
                        // Acesso Guru ou IAP
                        // O offset do plano tem de seguir a MESMA regra de hasValidPlan
                        // (planTitle && plan !== "0"): plano cancelado mantém planTitle mas
                        // não entra em `accesses`, e contá-lo desalinhava produto × plataforma/data.
                        const planOffset = row.planTitle && row.plan !== "0" ? 1 : 0;
                        const productIndex = i - ((row.coursesTitle?.length || 0) + planOffset);
                        const product = row.productIds?.[productIndex] || {};
                        const now = new Date();

                        if ((product.productId || product.transactionIdentifier) && !validade) {
                            statusAccess = 'Ativo';
                        } else {
                            statusAccess = validade && validade > now ? 'Ativo' : 'Acesso expirado';
                        }

                        // Busca plataforma (Guru, Pagarme, etc.) e data de compra
                        const { platform: fetchedPlatform, currentDataCycle: fetchedPurchaseDate } =
                            await fetchPlatformAndDate(userData, product);
                        platform = fetchedPlatform || "-";
                        currentDataCycle = fetchedPurchaseDate || "-";
                    }

                    // Se "access.name" for numérico (Guru) ou 20 chars (Pagarme), chamamos fetchProductName
                    let productName = access.name || '-';
                    const isGuru = /^\d+$/.test(productName);
                    const isPagarme = /^[A-Za-z0-9]{20}$/.test(productName);

                    if (isGuru || isPagarme) {
                        productName = await fetchProductName(productName);
                    }

                    selectedData.push({
                        Nome_Completo: row.Nome_Completo || '-',
                        Email: row.Email || '-',
                        Celular: row.Celular || '-',
                        CPF: row?.CPF || row?.Doc || '-',
                        Tipo_de_Acesso: statusAccess || '-',
                        Acesso: productName,
                        Plataforma: platform || '-',
                        Data_Ciclo_Atual: currentDataCycle || '-',
                        Data_Validade_Acesso: validade ? formatDateBR(validade) : '-',
                        Vídeos_Assistidos: row.watchedVideosCount ?? row.watchedContents?.length ?? 0,
                        Data_de_Cadastro: formatDateBR(getSignupDate(row)) || '-',
                        Forma_de_Cadastro: formatAccess(row) || '-',
                    });
                }
            }
        }

        const csvContent = generateCSVContent(headers, selectedData);
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', 'selected_data.csv');
        document.body.appendChild(link);
        link.click();
    }, [productAccessForCSV, fetchPlatformAndDate, formatAccess, fetchProductName]);

    const downloadAllUsers = useCallback(async () => {
        if (!isDownloading) {
            setIsDownloadAllModalVisible(true);
            setIsDownloading(true);
            const queue = new PQueue({ concurrency: 5 });
            let allUsers = [];
            let lastVisible = null;
            const batchSize = 500;

            try {
                while (true) {
                    const batch = await queue.add(async () => {
                        return fetchAllUsersHandler(batchSize, lastVisible);
                    });

                    if (batch.users.length === 0) break;

                    allUsers = allUsers.concat(batch.users);
                    lastVisible = batch.lastVisible;

                    if (batch.users.length < batchSize) break;
                }
                await handleDownloadCSV(allUsers);
            } catch (error) {
                console.error("Erro ao baixar todos os usuários:", error);
                alert("Ocorreu um erro ao baixar os usuários.");
            } finally {
                setIsDownloading(false);
                setIsDownloadAllModalVisible(false);
            }
        } else {
            alert("Arquivo está sendo processado");
        }
    }, [isDownloading, fetchAllUsersHandler, handleDownloadCSV]);

    const paginationComponentOptions = useMemo(() => ({
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
    }), []);

    const ExpandedComponent = useCallback(({ data }) => {
        const labelStyle = { fontFamily: ADMIN_FONT, fontSize: 11, color: ADMIN_COLORS.muted, marginBottom: 2 };
        const valueStyle = { fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.ink };
        const sectionTitle = { fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: '700', color: ADMIN_COLORS.ink, marginTop: 14, marginBottom: 8 };
        const chip = { borderWidth: 1, borderColor: ADMIN_COLORS.border, borderRadius: 8, paddingVertical: 6, paddingHorizontal: 10, backgroundColor: ADMIN_COLORS.surfaceSubtle };

        const videosCount = data.watchedVideosCount ?? data.watchedContents?.length ?? 0;
        const infoItems = [
            ['Celular', data?.Celular || 'não informado'],
            ['CPF / Documento', data?.CPF || data?.Doc || 'não informado'],
            // É a criação da CONTA no app (Auth), não a data da compra na
            // plataforma de pagamento: a cliente comparava as duas e achava que
            // o painel estava errado (ticket de suporte). O rótulo agora diz
            // qual das duas datas está sendo mostrada.
            [
                'Cadastro no app',
                getSignupDate(data) ? formatDateBR(getSignupDate(data)) : 'não informado',
                'criação da conta — não é a data da compra',
            ],
            ['Forma de cadastro', formatAccess(data)],
            ['Vídeos assistidos', String(videosCount)],
        ];

        return (
            <ViewTable style={{ margin: '0.5rem 0' }}>
                <Text style={sectionTitle}>Dados gerais</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {infoItems.map(([label, value, hint]) => (
                        <View key={label} style={{ minWidth: 180, maxWidth: 260, marginBottom: 10, marginRight: 24 }}>
                            <Text style={labelStyle}>{label}</Text>
                            <Text style={valueStyle}>{value}</Text>
                            {!!hint && <Text style={{ ...labelStyle, marginTop: 2, marginBottom: 0 }}>{hint}</Text>}
                        </View>
                    ))}
                </View>

                {data?.productIds?.length > 0 && (
                    <>
                        <Text style={sectionTitle}>Produtos com acesso</Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                            {data.productIds.map((product, index) => {
                                const info = productMap[String(product.productId)];
                                const name = info?.name || getProductName(product.productId);
                                const exp = product?.expirationDate?.toDate?.()
                                    || (product?.expirationDate instanceof Date ? product.expirationDate : null);
                                const isExpired = exp && exp < new Date();
                                return (
                                    <View
                                        key={index}
                                        style={[chip, isExpired && { backgroundColor: ADMIN_COLORS.dangerSoft, borderColor: ADMIN_COLORS.dangerBorder }]}
                                    >
                                        <Text style={valueStyle}>{name}</Text>
                                        <Text style={{ ...labelStyle, marginTop: 2, marginBottom: 0 }}>Código: {product.productId}</Text>
                                        {/* Distingue grant manual × compra: sem isso, dois acessos ao
                                            mesmo produto (manual expirado + assinatura ativa) eram
                                            indistinguíveis para o professor (ticket de suporte). */}
                                        <Text style={{ ...labelStyle, marginTop: 2, marginBottom: 0 }}>
                                            Origem: {getPlatformForProduct(product)}
                                        </Text>
                                        <Text
                                            style={{
                                                fontFamily: ADMIN_FONT,
                                                fontSize: 11,
                                                marginTop: 4,
                                                color: isExpired ? ADMIN_COLORS.danger : ADMIN_COLORS.muted,
                                            }}
                                        >
                                            {exp
                                                ? (isExpired
                                                    ? `Expirado em ${formatDateBR(product.expirationDate)}`
                                                    : `Expira em ${formatDateBR(product.expirationDate)}`)
                                                : 'Sem expiração'}
                                        </Text>
                                    </View>
                                );
                            })}
                        </View>
                    </>
                )}

                {data?.planTitle && data?.plan !== "0" && (
                    <>
                        <Text style={sectionTitle}>Plano da Stripe</Text>
                        <View style={chip}>
                            <Text style={valueStyle}>{data.planTitle}</Text>
                        </View>
                    </>
                )}

                {data?.coursesTitle?.length > 0 && data?.courses && (
                    <>
                        <Text style={sectionTitle}>Cursos da Stripe</Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                            {data.coursesTitle.map((course, index) => (
                                <View key={index} style={chip}>
                                    <Text style={valueStyle}>{course}</Text>
                                    <Text style={labelStyle}>
                                        {data.courses[index]?.dueDate ? `Expira em ${formatDateBR(data.courses[index].dueDate)}` : 'Sem expiração'}
                                    </Text>
                                </View>
                            ))}
                        </View>
                    </>
                )}
            </ViewTable>
        );
    }, [formatAccess, productMap]);

    return (
        <Provider>
            <ContainerScroll>
                {/* zIndex alto + position relative: o dropdown de autocomplete
                    (absoluto dentro desta linha) precisa pintar por cima dos
                    cards de indicadores que vêm logo abaixo. */}
                <ViewHorizontal style={{ flexWrap: 'wrap', gap: 12, position: 'relative', zIndex: 1000 }}>
                    <Title fontFamily={ADMIN_FONT} fontSize={28} fontWeight={700} color={ADMIN_COLORS.ink}>
                        Aluno(a)s:
                    </Title>
                    <Row gap="0.75rem">
                        <View style={{ position: 'relative', width: isMobile ? '100%' : 340, maxWidth: '100%', zIndex: 50 }}>
                        <View
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 8,
                                height: 40,
                                width: '100%',
                                borderWidth: 1,
                                borderColor: searchFocused ? ADMIN_COLORS.accent : ADMIN_COLORS.border,
                                borderRadius: 10,
                                backgroundColor: ADMIN_COLORS.surface,
                                paddingHorizontal: 12,
                                boxShadow: searchFocused ? '0 0 0 3px rgba(43, 147, 237, 0.16)' : 'none',
                            }}
                        >
                            <Ionicons name="search" size={16} color={ADMIN_COLORS.muted} />
                            <TextInput
                                value={searchText}
                                onChangeText={handleSearchChange}
                                onFocus={() => { setSearchFocused(true); if (suggestions.length) setShowSuggestions(true); }}
                                onBlur={() => { setSearchFocused(false); setTimeout(() => setShowSuggestions(false), 150); }}
                                onSubmitEditing={(e) => { debouncedFilter.cancel(); setShowSuggestions(false); handleFilter(e?.nativeEvent?.text ?? searchText); }}
                                placeholder="Pesquisar por nome ou e-mail"
                                placeholderTextColor={ADMIN_COLORS.placeholder}
                                style={{
                                    flex: 1,
                                    height: '100%',
                                    fontFamily: ADMIN_FONT,
                                    fontSize: 14,
                                    color: ADMIN_COLORS.ink,
                                    backgroundColor: 'transparent',
                                    borderWidth: 0,
                                    outlineStyle: 'none',
                                }}
                            />
                            {searchText !== '' && (
                                <TouchableOpacity onPress={clearSearch}>
                                    <Ionicons name="close" size={16} color={ADMIN_COLORS.muted} />
                                </TouchableOpacity>
                            )}
                        </View>
                        {showSuggestions && suggestions.length > 0 && (
                            <View
                                style={{
                                    position: 'absolute',
                                    top: 44,
                                    left: 0,
                                    right: 0,
                                    backgroundColor: ADMIN_COLORS.surface,
                                    borderWidth: 1,
                                    borderColor: ADMIN_COLORS.border,
                                    borderRadius: 10,
                                    paddingVertical: 4,
                                    boxShadow: '0 8px 24px rgba(16,24,40,0.14)',
                                    zIndex: 100,
                                    maxHeight: 320,
                                    overflow: 'auto',
                                }}
                            >
                                {suggestions.map((s) => (
                                    <TouchableOpacity
                                        key={s.id}
                                        onPress={() => pickSuggestion(s)}
                                        style={({ hovered }) => ({
                                            paddingVertical: 8,
                                            paddingHorizontal: 12,
                                            backgroundColor: hovered ? ADMIN_COLORS.surfaceSubtle : 'transparent',
                                            cursor: 'pointer',
                                        })}
                                    >
                                        <SmallText color={ADMIN_COLORS.ink} fontFamily={ADMIN_FONT} numberOfLines={1}>{s.name}</SmallText>
                                        <FooterText color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT} numberOfLines={1}>{s.email}</FooterText>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        )}
                        </View>
                        {/* A varredura NÃO roda sozinha: ela lê o documento de
                            todos os alunos, e disparar isso a cada abertura da
                            tela era a maior linha da fatura. O botão fica sempre
                            disponível — inclusive com número na tela, que é
                            justamente quando o admin precisa poder atualizar. */}
                        {onComputeStats ? (
                            <AdminButton
                                onPress={onComputeStats}
                                title={
                                    statsLoading
                                        ? 'Apurando…'
                                        : typeof stats?.active === 'number' ? 'Apurar de novo' : 'Apurar acessos'
                                }
                                subtitle={statsStatus}
                                subtitleStyle={
                                    stats?.stale && !statsLoading
                                        ? { color: ADMIN_COLORS.warnText, opacity: 1 }
                                        : null
                                }
                                icon="refresh-outline"
                                variant="secondary"
                                isLoading={statsLoading}
                                style={{ minHeight: 40, height: 40 }}
                            />
                        ) : null}
                        {/* 40px, a altura da busca e do "⋮" ao lado: com os 44 do
                            botão padrão ele ficava mais alto que a barra. */}
                        <AdminButton
                            onPress={() => setIsCreateUserModalVisible(true)}
                            title="Adicionar aluno"
                            icon="person-add-outline"
                            style={{ minHeight: 40 }}
                        />
                        <Menu
                            visible={menuVisible}
                            onDismiss={() => setMenuVisible(false)}
                            contentStyle={{ backgroundColor: ADMIN_COLORS.surface, borderRadius: 12 }}
                            anchor={
                                <TouchableOpacity
                                    onPress={() => setMenuVisible(true)}
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 10,
                                        borderWidth: 1,
                                        borderColor: ADMIN_COLORS.border,
                                        backgroundColor: ADMIN_COLORS.surface,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Ionicons name="ellipsis-vertical" size={18} color={ADMIN_COLORS.muted} />
                                </TouchableOpacity>
                            }
                        >
                            <Menu.Item
                                onPress={() => { setMenuVisible(false); downloadAllUsers(); }}
                                title="Baixar CSV completo"
                                titleStyle={{ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.ink, fontSize: 14 }}
                            />
                            <Menu.Item
                                onPress={() => { setMenuVisible(false); handleDownloadCSV(selectedRows); }}
                                title="Baixar CSV (selecionados)"
                                titleStyle={{ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.ink, fontSize: 14 }}
                            />
                            <Menu.Item
                                onPress={() => { setMenuVisible(false); showModal(); }}
                                title="Redefinir senha de aluno"
                                titleStyle={{ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.ink, fontSize: 14 }}
                            />
                        </Menu>
                    </Row>
                </ViewHorizontal>
                {/* Enquanto a varredura roda os cards mostram o PARCIAL (ou a
                    última apuração guardada) em vez de "…": numa base com
                    dezenas de milhares de alunos ela leva minutos, e a tela
                    parecia travada. `statKpi` só cai para "…" quando ainda não
                    existe número nenhum. */}
                <ViewHorizontal
                    justifyContent="center"
                    style={{ gap: 16, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}
                >
                    <ViewTable style={isMobile ? { width: '100%' } : { minWidth: 180, flexGrow: 1, maxWidth: 260 }}>
                        <FooterText textAlign="left" color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT}>Total de alunos cadastrados</FooterText>
                        <SubTitle textAlign="left" color={ADMIN_COLORS.ink} fontFamily={ADMIN_FONT}>{totalCount ?? '…'}</SubTitle>
                    </ViewTable>
                    <ViewTable style={isMobile ? { width: '100%' } : { minWidth: 180, flexGrow: 1, maxWidth: 260 }}>
                        <FooterText textAlign="left" color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT}>Com acesso ativo</FooterText>
                        <SubTitle textAlign="left" color={ADMIN_COLORS.success} fontFamily={ADMIN_FONT}>{statKpi(stats?.active)}</SubTitle>
                    </ViewTable>
                    <ViewTable style={isMobile ? { width: '100%' } : { minWidth: 180, flexGrow: 1, maxWidth: 260 }}>
                        <FooterText textAlign="left" color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT}>Sem compras</FooterText>
                        <SubTitle textAlign="left" color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT}>{statKpi(stats?.noPurchase)}</SubTitle>
                    </ViewTable>
                    <ViewTable style={isMobile ? { width: '100%' } : { minWidth: 180, flexGrow: 1, maxWidth: 260 }}>
                        <FooterText textAlign="left" color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT}>Acesso expirado</FooterText>
                        <SubTitle textAlign="left" color={ADMIN_COLORS.danger} fontFamily={ADMIN_FONT}>{statKpi(stats?.expired)}</SubTitle>
                    </ViewTable>
                </ViewHorizontal>
                <DataTable
                    columns={columns}
                    data={tableData}
                    highlightOnHover
                    pointerOnHover
                    selectableRows
                    selectableRowsHighlight
                    selectableRowsVisibleOnly
                    customStyles={customStyles}
                    onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                    onChangePage={handlePageChange}
                    pagination
                    paginationRowsPerPageOptions={[10]}
                    paginationServer={!isSearchResults}
                    progressPending={loading}
                    progressComponent={
                        <ActivityIndicator
                            style={{
                                flex: 1,
                                backgroundColor: "#FFFFFF",
                                justifyContent: "center",
                            }}
                            color="#d3d3d3"
                            size="large"
                        />
                    }
                    paginationDefaultPage={1}
                    paginationResetDefaultPage={resetPaginationToggle}
                    paginationTotalRows={totalFiltered}
                    paginationComponentOptions={paginationComponentOptions}
                    paginationIconFirstPage={false}
                    paginationIconLastPage={false}
                    expandableRows
                    expandableRowsComponent={ExpandedComponent}
                />
                {isCreateUserModalVisible && products &&
                    <CreateUserModal
                        products={products}
                        visible={isCreateUserModalVisible}
                        onClose={() => setIsCreateUserModalVisible(false)}
                        onSave={handleSaveUser}
                    />
                }
                <Modal
                    visible={isDownloadAllModalVisible}
                    contentContainerStyle={containerStyle}
                >
                    <View>
                        <ActivityIndicator
                            style={{ flex: 1, backgroundColor: "#FFFFFF", justifyContent: "center" }}
                            color="#666666"
                            size="large"
                        />
                        <br />
                        <br />
                        <SmallText color="black">
                            Processando arquivo, aguarde alguns minutos...
                        </SmallText>
                    </View>
                </Modal>
                <Portal>
                    <Modal
                        visible={visible}
                        onDismiss={hideModal}
                        contentContainerStyle={containerStyle}
                    >
                        <ResetPassword isAdmin={true} />
                    </Modal>
                </Portal>
            </ContainerScroll>
        </Provider>
    );
};

export default Table;